import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Producto } from '@tanteo/compartido';
import { formatearCOP, sumarTarifas } from '@tanteo/compartido';
import { listarProductos } from '../../lib/api';
import { actualizarProducto, crearProducto, suprimirProductoRpc, type ProductoEntrada } from '../../lib/api-catalogo';
import { useApp } from '../../lib/contexto';
import { BarraDePagina, Boton, Campo, CampoNumero, CeldaPlata, EncabezadoTabla, Esqueleto, EstadoVacio, FilaAcciones, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, ThAcciones } from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { listarImpuestos, type Impuesto } from '../../lib/api-cuentas';
import { ImportarCsv } from './ImportarCsv';

const VACIO: ProductoEntrada = { sku: '', ean: null, nombre: '', categoria: '', precio: 0, impuestoIds: [], activo: true };

export function Catalogo() {
  const { negocio } = useApp();
  const esPropietario = negocio?.rol === 'propietario';
  const [productos, setProductos] = useState<Producto[] | null>(null);
  const [filtro, setFiltro] = useState('');
  const [editando, setEditando] = useState<{ id: string | null; datos: ProductoEntrada } | null>(null);
  const [suprimiendo, setSuprimiendo] = useState<Producto | null>(null);
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [importando, setImportando] = useState(false);
  const [avisoTop, setAvisoTop] = useState('');
  const [impuestos, setImpuestos] = useState<Impuesto[]>([]);

  function cargar() {
    if (!negocio) return;
    listarProductos(negocio.negocioId)
      .then(setProductos)
      .catch(() => setProductos([]));
  }
  useEffect(cargar, [negocio]);

  useEffect(() => {
    if (!negocio) return;
    listarImpuestos(negocio.negocioId)
      .then(setImpuestos)
      .catch(() => setImpuestos([]));
  }, [negocio]);

  // Las categorías ya usadas se ofrecen como sugerencia.
  const categorias = useMemo(
    () => [...new Set((productos ?? []).map((p) => p.categoria).filter(Boolean))].sort(),
    [productos]
  );

  const visibles = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return productos ?? [];
    return (productos ?? []).filter(
      (p) => p.nombre.toLowerCase().includes(f) || p.sku.toLowerCase().includes(f) || p.categoria.toLowerCase().includes(f)
    );
  }, [productos, filtro]);

  async function guardar() {
    if (!negocio || !editando) return;
    setOcupado(true);
    setAviso('');
    try {
      if (editando.id) await actualizarProducto(negocio.negocioId, editando.id, editando.datos);
      else await crearProducto(negocio.negocioId, editando.datos);
      setEditando(null);
      cargar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      setOcupado(false);
    }
  }

  async function suprimir() {
    if (!suprimiendo) return;
    setOcupado(true);
    setAviso('');
    try {
      await suprimirProductoRpc(suprimiendo.id);
      setAvisoTop(`Producto «${suprimiendo.nombre}» eliminado.`);
      setSuprimiendo(null);
      cargar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo eliminar.');
    } finally {
      setOcupado(false);
    }
  }

  if (!negocio) return null;

  return (
    <div className="space-y-4">
      <BarraDePagina titulo="Catálogo">
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-56">
            <Campo placeholder="Buscar…" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
          </div>
          <Link to="/carta" className="rounded-control bg-surface px-4 py-2 font-bold min-h-(--alto-control) inline-flex items-center">
            ⎙ Carta de códigos
          </Link>
          {esPropietario && (
            <Boton variante="contorno" onClick={() => setImportando(true)} data-e2e="importar-csv">
              Importar CSV
            </Boton>
          )}
          {esPropietario && (
            <Boton onClick={() => setEditando({ id: null, datos: { ...VACIO } })} data-e2e="nuevo-producto">
              + Producto
            </Boton>
          )}
        </div>
      </BarraDePagina>

      {/* El aviso para un lector de pantalla: la región existe desde el montaje, fuera del flujo. */}
      <p role="status" className="sr-only">
        {avisoTop}
      </p>
      {avisoTop && (
        <p className="text-success bg-success/10 rounded-control px-3 py-2 font-semibold" data-e2e="aviso-catalogo">
          {avisoTop}
        </p>
      )}

      <Tarjeta>
        {productos === null ? (
          <Esqueleto lineas={8} />
        ) : visibles.length === 0 ? (
          <EstadoVacio titulo="Sin productos" detalle="Crea el primero o importa tu catálogo desde un archivo CSV." />
        ) : (
          <TablaAncha>
              <EncabezadoTabla>
                  <th className="py-2 pr-2">SKU</th>
                  <th className="py-2 pr-2">Categoría</th>
                  <th className="py-2 pr-2">Producto</th>
                  <th className="py-2 pr-2 text-right">Precio</th>
                  <th className="py-2 pr-2 text-right">Imp. %</th>
                  <th className="py-2 pr-2">Estado</th>
                  {esPropietario && <ThAcciones className="w-20" />}
              </EncabezadoTabla>
              <tbody>
                {visibles.map((p) => (
                  <FilaTabla key={p.id}>
                    <td className="num py-2 pr-2 font-bold">{p.sku}</td>
                    <td className="py-2 pr-2">{p.categoria}</td>
                    <td className="py-2 pr-2">{p.nombre}</td>
                    <CeldaPlata>{formatearCOP(p.precio)}</CeldaPlata>
                    <CeldaPlata>{sumarTarifas(p.impuestos)}</CeldaPlata>
                    <td className="py-2 pr-2">{p.activo ? <Insignia tono="exito">Activo</Insignia> : <Insignia tono="neutro">Inactivo</Insignia>}</td>
                    {esPropietario && (
                      <td className="py-2 text-right">
                        <span className="inline-flex justify-end gap-3">
                          <button
                            className="text-info underline"
                            onClick={() =>
                              setEditando({
                                id: p.id,
                                datos: { sku: p.sku, ean: p.ean, nombre: p.nombre, categoria: p.categoria, precio: p.precio, impuestoIds: p.impuestos.map((i) => i.id), activo: p.activo },
                              })
                            }
                          >
                            Editar
                          </button>
                          {!p.activo && (
                            <button
                              className="text-destructive underline"
                              data-e2e="prod-eliminar"
                              onClick={() => {
                                setAviso('');
                                setSuprimiendo(p);
                              }}
                            >
                              Eliminar
                            </button>
                          )}
                        </span>
                      </td>
                    )}
                  </FilaTabla>
                ))}
              </tbody>
            </TablaAncha>
        )}
      </Tarjeta>

      <Modal abierto={editando !== null} titulo={editando?.id ? 'Editar producto' : 'Nuevo producto'} onCerrar={() => setEditando(null)}>
        {editando && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo etiqueta="SKU (se escanea)" value={editando.datos.sku} disabled={!!editando.id}
                ayuda="Tu código propio: el que imprimes en la carta y escanea el mostrador."
                onChange={(e) => setEditando({ ...editando, datos: { ...editando.datos, sku: e.target.value } })} data-e2e="prod-sku" />
              <Campo etiqueta="Código de barras (EAN, opcional)" value={editando.datos.ean ?? ''} placeholder="7701234567890"
                ayuda="El código de barras que ya trae el producto empacado de fábrica. También se puede escanear."
                onChange={(e) => setEditando({ ...editando, datos: { ...editando.datos, ean: e.target.value || null } })} />
            </div>
            <Campo etiqueta="Nombre" value={editando.datos.nombre}
              onChange={(e) => setEditando({ ...editando, datos: { ...editando.datos, nombre: e.target.value } })} data-e2e="prod-nombre" />
            <div className="grid grid-cols-2 gap-3">
              <>
                <Campo etiqueta="Categoría" list="categorias-usadas" value={editando.datos.categoria}
                  onChange={(e) => setEditando({ ...editando, datos: { ...editando.datos, categoria: e.target.value } })} data-e2e="prod-categoria" />
                <datalist id="categorias-usadas">
                  {categorias.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </>
              <CampoNumero etiqueta="Precio (COP)" plata min={0} valor={editando.datos.precio} className="num"
                onValor={(n) => setEditando({ ...editando, datos: { ...editando.datos, precio: n ?? 0 } })} data-e2e="prod-precio" />
            </div>

            {/* Los impuestos van en su propia fila y en horizontal: apilados en una columna
                el formulario crecería hacia abajo con cada tributo nuevo. */}
            <fieldset data-e2e="prod-impuesto">
              <legend className="text-muted mb-1 block text-contenido font-semibold">Impuestos</legend>
              {impuestos.length === 0 ? (
                <p className="text-muted text-contenido">
                  Sin impuestos configurados: el producto queda exento. Se crean en Configuración.
                </p>
              ) : (
                <div className="flex flex-wrap gap-x-5 gap-y-1">
                  {impuestos.map((i) => (
                    <label key={i.id} className="flex items-center gap-2 text-contenido">
                      <input
                        type="checkbox"
                        checked={editando.datos.impuestoIds.includes(i.id)}
                        onChange={(e) =>
                          setEditando({
                            ...editando,
                            datos: {
                              ...editando.datos,
                              impuestoIds: e.target.checked
                                ? [...editando.datos.impuestoIds, i.id]
                                : editando.datos.impuestoIds.filter((x: string) => x !== i.id),
                            },
                          })
                        }
                      />
                      {i.nombre} ({i.tarifa}%)
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
            <label className="flex items-center gap-2 text-contenido font-semibold">
              <input type="checkbox" checked={editando.datos.activo}
                onChange={(e) => setEditando({ ...editando, datos: { ...editando.datos, activo: e.target.checked } })} />
              Activo (visible en el mostrador)
            </label>
            <MensajeError>{aviso}</MensajeError>
            <FilaAcciones>
              <Boton variante="contorno" onClick={() => setEditando(null)} disabled={ocupado}>Volver</Boton>
              <Boton onClick={() => void guardar()} disabled={ocupado || !editando.datos.sku || !editando.datos.nombre || !editando.datos.categoria} data-e2e="prod-guardar">
                {ocupado ? 'Guardando…' : 'Guardar'}
              </Boton>
            </FilaAcciones>
          </div>
        )}
      </Modal>

      <Modal abierto={suprimiendo !== null} titulo="Eliminar producto" onCerrar={() => setSuprimiendo(null)}>
        {suprimiendo && (
          <>
            <p className="mb-3">
              <strong>{suprimiendo.nombre}</strong> · <span className="num font-bold">{suprimiendo.sku}</span> ·{' '}
              {suprimiendo.categoria} · <span className="num">{formatearCOP(suprimiendo.precio)}</span>
            </p>
            <p className="text-muted mb-1 text-contenido">
              El producto sale del catálogo, de la botonera y del escaneo. No se puede deshacer desde la aplicación.
            </p>
            <p className="text-muted mb-1 text-contenido">
              Los comprobantes ya emitidos no cambian: cada venta congeló el nombre y el precio al emitirse.
            </p>
            <p className="text-muted mb-4 text-contenido">
              El SKU queda libre para un producto nuevo. Si más adelante importas un CSV que trae este SKU, el
              producto se crea de nuevo.
            </p>
            <MensajeError e2e="eliminar-error">{aviso}</MensajeError>
            <FilaAcciones>
              <Boton variante="contorno" onClick={() => setSuprimiendo(null)} disabled={ocupado}>
                Volver
              </Boton>
              <Boton variante="peligro" onClick={() => void suprimir()} disabled={ocupado} data-e2e="eliminar-definitivo">
                {ocupado ? 'Eliminando…' : 'Eliminar definitivamente'}
              </Boton>
            </FilaAcciones>
          </>
        )}
      </Modal>

      <ImportarCsv
        negocioId={negocio.negocioId}
        abierto={importando}
        onCerrar={() => setImportando(false)}
        onImportado={(n) => {
          setAvisoTop(`${n} producto${n === 1 ? '' : 's'} importado${n === 1 ? '' : 's'}.`);
          cargar();
        }}
      />
    </div>
  );
}
